import React, { useContext, useEffect, useState } from "react";
import { AuthContext } from "../../components/user/BCPAuthProvider";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  CircularProgress,
  Container,
  Grid,
  Link,
  Typography,
} from "@mui/material";

import ModalLoadingFull from "../../components/modals/ModalLoadingFull";
import { postNotification } from "../../components/utilities/Notifications";
import ListResponsive from "../../components/other/ListResponsive";

import moment from "moment";
import "moment-timezone";
import StoreOrderNotes from "./components/StoreOrderNotes";
import { getData } from "../../components/utilities/UtilityFunctions";

const fetchFile = async (fileId) => {
  if (!fileId) return;

  var returnObj;
  try {
    returnObj = await getData(`/files/${fileId}`, {
      queryStringParameters: {},
    });
  } catch (err) {
    console.error(err);
  }
  return returnObj;
};

const ImagePreview = (props) => {
  const { fileId, alt, outerSX } = props;
  const [loading, setLoading] = useState(true);
  const [image, setImage] = useState(null);
  useEffect(() => {
    const go = async () => {
      setLoading(true);
      if (!fileId) {
        setImage(null);
        setLoading(false);
        return;
      }

      if (image) {
        if (image.id === fileId) {
          setLoading(false);
          return;
        }
        setImage(null);
      }

      try {
        var file = await fetchFile(fileId);
        if (file) {
          setImage({
            id: fileId,
            ...file,
          });
        }
      } catch (err) {
        //
      }
      setLoading(false);
    };

    go();
    // eslint-disable-next-line
  }, [fileId]);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "50px",
        minHeight: "50px",
        maxHeight: "50px",
        p: 1,
        border: 1,
        borderColor: "divider",
        ...outerSX,
      }}
    >
      {!loading && image?.url && (
        <img
          src={image.url}
          alt={alt}
          style={{ maxWidth: "100%", maxHeight: "100%" }}
        />
      )}

      {loading && <CircularProgress size={24} />}
    </Box>
  );
};

export default function StoreOrder() {
  const authContext = useContext(AuthContext);
  const navigate = useNavigate();

  let { transactionId } = useParams();

  const [transaction, setTransaction] = useState([]);

  const [loading, setLoading] = useState(true);
  const [totals, setTotals] = useState([]);

  useEffect(() => {
    const go = async () => {
      try {
        var apiTransaction = await getData(
          `/storetransactions/${transactionId}`,
          {
            queryStringParameters: {},
          }
        );

        var newTotals = [];
        var newTotal = { label: "Total", qty: 0, value: 0 };
        if (apiTransaction?.lineItems?.length) {
          apiTransaction.lineItems.forEach((item) => {
            newTotal.qty += item.qty;
            newTotal.value += item.amount;
          });
        }
        newTotals.push(newTotal);
        setTotals(newTotals);

        setTransaction(apiTransaction);
      } catch (err) {
        postNotification("error", err);
        navigate("/home");
        setLoading(false);
        return;
      }
      setLoading(false);
    };

    go();
    // eslint-disable-next-line
  }, [authContext]);

  return (
    <>
      {!loading && (
        <Container sx={{ pt: 2, pb: 2 }} maxWidth="lg">
          <Grid container sx={{ gap: 2 }}>
            <Grid item xs={12} sx={{ p: 2 }}>
              <Typography variant="h3">
                {transaction?.transactionType || "Transaction"} #
                {transaction?.orderId?.toUpperCase()}
              </Typography>
            </Grid>

            <Grid item xs={12} sx={{ p: 2 }}>
              <Grid container spacing={1}>
                <Grid item xs={3}>
                  <Typography variant="body1">Transaction Type</Typography>
                </Grid>
                <Grid item xs={9}>
                  <Typography variant="body1">
                    {transaction?.transactionType}
                  </Typography>
                </Grid>

                <Grid item xs={3}>
                  <Typography variant="body1">Date</Typography>
                </Grid>
                <Grid item xs={9}>
                  <Typography variant="body1">
                    {moment(transaction?.created_at).format("LLLL")}
                  </Typography>
                </Grid>

                <Grid item xs={3}>
                  <Typography variant="body1">Last Update</Typography>
                </Grid>
                <Grid item xs={9}>
                  <Typography variant="body1">
                    {moment(transaction?.updated_at).format("LLLL")}
                  </Typography>
                </Grid>

                <Grid item xs={3}>
                  <Typography variant="body1">Status</Typography>
                </Grid>
                <Grid item xs={9}>
                  <Typography variant="body1">{transaction?.status}</Typography>
                </Grid>

                {(transaction.transactionType === "Adjustment" ||
                  transaction.transactionType === "Judging" ||
                  transaction.transactionType === "Refund") && (
                  <>
                    <Grid item xs={3}>
                      <Typography variant="body1">Amount</Typography>
                    </Grid>
                    <Grid item xs={9}>
                      <Typography variant="body1">
                        {transaction?.amount} LP
                      </Typography>
                    </Grid>
                  </>
                )}
                {transaction?.summary && (
                  <>
                    <Grid item xs={3}>
                      <Typography variant="body1">Summary</Typography>
                    </Grid>
                    <Grid item xs={9}>
                      <Typography variant="body1">
                        {transaction.summary.split("\n").map((i, key) => {
                          return <Box key={key}>{i}</Box>;
                        })}
                      </Typography>
                    </Grid>
                  </>
                )}
                {transaction?.deliveryAddress && (
                  <>
                    <Grid item xs={3}>
                      <Typography variant="body1">Deliver To</Typography>
                    </Grid>
                    <Grid item xs={9}>
                      <Typography variant="body1">{`${transaction?.deliveryAddress?.firstName} ${transaction?.deliveryAddress?.lastName}`}</Typography>
                      {transaction?.deliveryAddress?.company &&
                        transaction?.deliveryAddress?.company !== "" && (
                          <Typography variant="body1">
                            {transaction?.deliveryAddress?.company}
                          </Typography>
                        )}
                      <Typography variant="body1">
                        {transaction?.deliveryAddress?.formatted_address}
                      </Typography>
                    </Grid>
                  </>
                )}
              </Grid>
            </Grid>
            {transaction?.notes?.length > 0 && (
              <Grid item xs={12} sx={{ p: 2 }}>
                <StoreOrderNotes
                  transaction={transaction}
                  setTransaction={setTransaction}
                />
              </Grid>
            )}
            {transaction?.lineItems?.length > 0 && (
              <Grid item xs={12} sx={{ p: 2 }}>
                <ListResponsive
                  originalList={transaction?.lineItems || []}
                  rowProps={{
                    headings: <RowItem heading />,
                    mapFunction: (row, index) => (
                      <RowItem key={index} row={row} />
                    ),
                  }}
                />

                {totals?.length > 0 &&
                  totals.map((total, index) => {
                    return <RowItem key={index} total={total} />;
                  })}
              </Grid>
            )}
          </Grid>
        </Container>
      )}
      {loading && <ModalLoadingFull />}
    </>
  );
}

const RowItem = (props) => {
  const { heading, row, total } = props;

  return (
    <Grid
      item
      xs={12}
      sx={{
        p: 1,
        mb: 1,
        border: "1px solid rgba(0, 0, 0, 0.12)",
        borderRadius: 1,
        boxShadow: 1,
      }}
    >
      <Grid container spacing={1}>
        <Grid item xs={6}>
          {heading ? (
            <Typography variant="body1">Product</Typography>
          ) : total ? (
            <Typography variant="body1">{total.label || ""}</Typography>
          ) : (
            <Grid container>
              <Grid item xs sx={{ maxWidth: ["50px", "70px"] }}>
                <ImagePreview
                  fileId={row?.product?.imageFileId}
                  alt={row?.product.name}
                />
              </Grid>
              <Grid item xs>
                <Link
                  href={`/shop/product/${row?.product.id}`}
                  underline="none"
                >
                  <Typography variant="body1">{row?.product.name}</Typography>
                </Link>
              </Grid>
            </Grid>
          )}
        </Grid>
        <Grid item xs={2}>
          {heading ? (
            <Typography variant="body1">Price</Typography>
          ) : total ? (
            <></>
          ) : (
            <Typography variant="body1">{row.price} LP</Typography>
          )}
        </Grid>
        <Grid
          item
          xs={2}
          sx={{
            textAlign: "center",
          }}
        >
          {heading ? (
            <Typography variant="body1">Quantity</Typography>
          ) : total ? (
            <Typography variant="body1">{total.qty}</Typography>
          ) : (
            <Typography variant="body1">{row.qty}</Typography>
          )}
        </Grid>
        <Grid
          item
          xs={2}
          sx={{
            textAlign: "right",
          }}
        >
          {heading ? (
            <Typography variant="body1">Subtotal</Typography>
          ) : total ? (
            <Typography variant="body1">{total.value} LP</Typography>
          ) : (
            <Typography variant="body1">{row.amount} LP</Typography>
          )}
        </Grid>
      </Grid>
    </Grid>
  );
};
